import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Share2, X, Copy, Check, BarChart2, IndianRupee, TrendingUp, Target, Shuffle, Sprout, Layers, Clock, BookOpen, Scale, Star, ChevronDown } from 'lucide-react';
import SmartCalculatorsSidebar from '../../components/common/SmartCalculatorsSidebar';
import Navbar from '../../components/common/Navbar';

const RETURN_PRESETS = [6, 8, 10, 12, 14];

const SIPCalculator = () => {
  const [mode, setMode] = useState('sip');
  const [monthlyAmtStr, setMonthlyAmtStr]   = useState('10000'); // string so user can clear/type freely
  const [lumpsumAmtStr, setLumpsumAmtStr]   = useState('100000');
  const [returnRateStr, setReturnRateStr]   = useState('12');    // string — lets user type "12.5"
  const [timePeriod, setTimePeriod]         = useState(10);

  // Derived numeric amounts (0 when field is empty)
  const monthlyAmount = parseInt(monthlyAmtStr) || 0;
  const lumpsumAmount = parseInt(lumpsumAmtStr) || 0;
  const [showShareMenu, setShowShareMenu]   = useState(false);
  const [copied, setCopied]                 = useState(false);
  const [openFaq, setOpenFaq]               = useState(null);

  // Clamped numeric rate — used everywhere except the input display
  const returnRate = Math.min(Math.max(parseFloat(returnRateStr) || 1, 1), 30);

  // ── Calculation ────────────────────────────────────────────────────────────
  const { investedAmount, estReturns, totalValue } = useMemo(() => {
    const years = timePeriod;
    if (mode === 'sip') {
      const months = years * 12;
      const i      = returnRate / 1200;
      const inv    = monthlyAmount * months;
      if (i === 0) return { investedAmount: inv, estReturns: 0, totalValue: inv };
      const total  = monthlyAmount * ((Math.pow(1 + i, months) - 1) / i) * (1 + i);
      return { investedAmount: Math.round(inv), estReturns: Math.round(total - inv), totalValue: Math.round(total) };
    } else {
      const r     = returnRate / 100;
      const inv   = lumpsumAmount;
      const total = lumpsumAmount * Math.pow(1 + r, years);
      return { investedAmount: Math.round(inv), estReturns: Math.round(total - inv), totalValue: Math.round(total) };
    }
  }, [mode, monthlyAmount, lumpsumAmount, returnRate, timePeriod]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const fmt = (n) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

  const sliderBg = (val, min, max) => {
    const pct = ((val - min) / (max - min)) * 100;
    return `linear-gradient(to right, #115e59 0%, #115e59 ${pct}%, #e5e7eb ${pct}%, #e5e7eb 100%)`;
  };

  const handleShare = (platform) => {
    const text = encodeURIComponent('Plan your SIP investments with Kapitalised: kapitalised.com/calculators/sip');
    const maps = {
      whatsapp: `https://wa.me/?text=${text}`,
      twitter:  `https://twitter.com/intent/tweet?text=${text}`,
    };
    if (platform === 'copy') {
      navigator.clipboard.writeText('https://kapitalised.com/calculators/sip');
      setCopied(true); setTimeout(() => setCopied(false), 2000); return;
    }
    window.open(maps[platform], '_blank', 'width=600,height=400');
    setShowShareMenu(false);
  };

  // Input handlers
  const handleAmtInput = (raw) => {
    // Allow free typing including empty / 0
    const cleaned = raw.replace(/[^0-9]/g, '');
    mode === 'sip' ? setMonthlyAmtStr(cleaned) : setLumpsumAmtStr(cleaned);
  };

  const handleReturnInput = (raw) => {
    // Allow digits + one decimal point (e.g. "12.", "12.5", ".5")
    const cleaned = raw.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
    setReturnRateStr(cleaned);
  };

  const setPeriod = (raw) => {
    const n = parseInt(String(raw).replace(/[^0-9]/g, '')) || 1;
    setTimePeriod(Math.min(Math.max(n, 1), 40));
  };

  // ── Donut chart math ───────────────────────────────────────────────────────
  const R = 70, CX = 100, CY = 100;
  const circ    = 2 * Math.PI * R;
  const gap     = 5;
  const invPct  = totalValue > 0 ? investedAmount / totalValue : 0.5;
  const retPct  = totalValue > 0 ? estReturns / totalValue : 0.5;
  const invDash = Math.max(invPct * circ - gap, 0);
  const retDash = Math.max(retPct * circ - gap, 0);

  // ── Slider range constants ─────────────────────────────────────────────────
  const amtMin  = mode === 'sip' ? 500      : 1000;
  const amtMax  = mode === 'sip' ? 100000   : 10000000;
  const amtStep = mode === 'sip' ? 500      : 1000;
  const curAmtStr  = mode === 'sip' ? monthlyAmtStr  : lumpsumAmtStr;
  const curAmt     = mode === 'sip' ? monthlyAmount  : lumpsumAmount;
  // Slider value clamped to its valid range (input can go below min / be empty)
  const sliderAmt  = Math.min(Math.max(curAmt, amtMin), amtMax);

  // ── Style tokens ───────────────────────────────────────────────────────────
  const labelSt    = { fontFamily: 'Onest, sans-serif', fontSize: '14px', fontWeight: 500, color: '#374151' };
  const valueSt    = { fontFamily: 'Onest, sans-serif', fontSize: '15px', fontWeight: 600, color: '#115e59', border: 'none', outline: 'none', background: 'transparent', textAlign: 'right' };
  const suffixSt   = { fontFamily: 'Onest, sans-serif', fontSize: '15px', fontWeight: 500, color: '#9ca3af' };
  const minMaxSt   = { fontFamily: 'Onest, sans-serif', fontSize: '11px', color: '#9ca3af' };
  const inputBoxSt = { display: 'flex', alignItems: 'center', gap: '3px', backgroundColor: '#f9fafb', border: '1.5px solid #e5e7eb', borderRadius: '8px', padding: '6px 12px' };

  const css = `
    .sip-slider { -webkit-appearance: none; appearance: none; width: 100%; height: 5px; border-radius: 3px; outline: none; cursor: pointer; }
    .sip-slider::-webkit-slider-thumb { -webkit-appearance: none; width: 20px; height: 20px; border-radius: 50%; background: #fff; border: 2.5px solid #115e59; cursor: pointer; box-shadow: 0 1px 6px rgba(0,0,0,0.15); }
    .sip-slider::-moz-range-thumb { width: 20px; height: 20px; border-radius: 50%; background: #fff; border: 2.5px solid #115e59; cursor: pointer; box-shadow: 0 1px 6px rgba(0,0,0,0.15); }
    .no-spin::-webkit-outer-spin-button, .no-spin::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
    .no-spin { -moz-appearance: textfield; }
    .sip-card { padding: 36px; }
    .sip-chart-container { flex-shrink: 0; }
    .sip-seo { background-color: #ffffff; padding: 48px 450px 64px 100px; }
    .seo-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    @media (min-width: 640px) and (max-width: 1023px) {
      .hero-section { padding: 32px 40px 80px 40px !important; }
      .sip-seo { padding: 48px 40px 64px 40px !important; }
      .sip-card { padding: 28px !important; }
    }
    @media (max-width: 639px) {
      .sip-two-col { flex-direction: column !important; }
      .sip-chart-row { flex-direction: column !important; align-items: stretch !important; }
      .sip-chart-container { width: 100%; display: flex; justify-content: center; }
      .sip-card { padding: 20px !important; }
      .hero-section { padding: 32px 20px 60px 20px !important; }
      .sip-seo { padding: 32px 20px 48px 20px !important; }
      .seo-grid-2 { grid-template-columns: 1fr !important; }
    }
  `;

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <link href="https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Onest:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" />
      <style>{css}</style>

      <Navbar background="#1a4d3e" />

      {/* ── Hero Section with Gradient ── */}
      <div className="hero-section" style={{ background: 'linear-gradient(180deg, #1a4d3e 0%, #2d6b5a 30%, #4a9d7f 60%, #7ec9a8 85%, #ffffff 100%)', padding: '32px 100px 80px 100px' }}>

        {/* Breadcrumb */}
        <div style={{ marginBottom: '18px' }}>
          <Link to="/" style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.75)', textDecoration: 'none', fontWeight: 400 }}>Home</Link>
          <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.45)', margin: '0 6px' }}>/</span>
          <Link to="/" style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.55)', textDecoration: 'none', fontWeight: 400 }}>Calculators</Link>
          <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.45)', margin: '0 6px' }}>/</span>
          <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.45)' }}>SIP Calculator</span>
        </div>

        {/* Title + share */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
          <h1
            className="landing-title"
            style={{ fontFamily: 'Instrument Serif, serif', fontWeight: 400, fontSize: '62px', color: '#ffffff', margin: 0, lineHeight: 1 }}
          >
            SIP Calculator
          </h1>

          {/* Share button */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowShareMenu(!showShareMenu)}
              style={{ fontFamily: 'Onest, sans-serif', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: '#ffffff', fontWeight: 600, fontSize: '14px', paddingBottom: '8px' }}
            >
              <Share2 size={16} /> SHARE
            </button>
            {showShareMenu && (
              <div style={{ position: 'absolute', right: 0, top: '36px', background: '#fff', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', border: '1px solid #f3f4f6', padding: '12px', zIndex: 50, minWidth: '220px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ fontFamily: 'Onest', fontWeight: 600, fontSize: '13px', color: '#111827' }}>Share via</span>
                  <button onClick={() => setShowShareMenu(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}><X size={16} /></button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                  {[
                    { id: 'whatsapp', label: 'WhatsApp', bg: '#25D366', icon: <svg width="20" height="20" fill="white" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg> },
                    { id: 'twitter',  label: 'X',        bg: '#000',     icon: <svg width="16" height="16" fill="white" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
                    { id: 'copy',     label: copied ? 'Copied!' : 'Copy', bg: '#e5e7eb', icon: copied ? <Check size={18} color="#16a34a" /> : <Copy size={18} color="#374151" /> },
                  ].map(({ id, label, bg, icon }) => (
                    <button key={id} onClick={() => handleShare(id)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '8px', borderRadius: '8px', background: 'none', border: 'none', cursor: 'pointer' }}>
                      <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</div>
                      <span style={{ fontFamily: 'Onest', fontSize: '11px', color: '#6b7280' }}>{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Two-column layout ── */}
        <div className="sip-two-col" style={{ display: 'flex', gap: '24px', alignItems: 'flex-start', minWidth: 0 }}>

          {/* LEFT — Calculator card */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="sip-card" style={{ backgroundColor: '#ffffff', borderRadius: '24px', boxShadow: '0 8px 48px rgba(0,0,0,0.18)' }}>

              {/* SIP / Lumpsum toggle — separate capsules */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '36px' }}>
                {['sip', 'lumpsum'].map((m) => (
                  <button
                    key={m}
                    onClick={() => setMode(m)}
                    style={{
                      fontFamily: 'Onest, sans-serif', fontWeight: 600, fontSize: '14px',
                      padding: '9px 28px', borderRadius: '50px', cursor: 'pointer',
                      backgroundColor: mode === m ? '#115e59' : '#f3f4f6',
                      color: mode === m ? '#ffffff' : '#6b7280',
                      border: mode === m ? 'none' : '1.5px solid #e5e7eb',
                      transition: 'all 0.18s ease',
                    }}
                  >
                    {m === 'sip' ? 'SIP' : 'Lumpsum'}
                  </button>
                ))}
              </div>

              {/* ── Amount slider ── */}
              <SliderRow
                label={mode === 'sip' ? 'Monthly Investment' : 'Total Investment'}
                value={sliderAmt}
                displayValue={curAmtStr}
                min={amtMin} max={amtMax} step={amtStep}
                displayPrefix="₹"
                minLabel={`₹${amtMin.toLocaleString('en-IN')}`}
                maxLabel={`₹${amtMax.toLocaleString('en-IN')}`}
                onSlider={(v) => mode === 'sip' ? setMonthlyAmtStr(String(v)) : setLumpsumAmtStr(String(v))}
                onInput={handleAmtInput}
                sliderBg={sliderBg}
                labelSt={labelSt} valueSt={valueSt} suffixSt={suffixSt} minMaxSt={minMaxSt} inputBoxSt={inputBoxSt}
              />

              {/* ── Expected Return slider + presets ── */}
              <div style={{ marginBottom: '30px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <label style={labelSt}>Expected Return (p.a.)</label>
                  <div style={inputBoxSt}>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="no-spin"
                      value={returnRateStr}
                      onChange={(e) => handleReturnInput(e.target.value)}
                      onBlur={() => {
                        // on blur — normalise: clamp and strip trailing dot
                        const n = Math.min(Math.max(parseFloat(returnRateStr) || 1, 1), 30);
                        setReturnRateStr(String(n));
                      }}
                      style={{ ...valueSt, width: '48px' }}
                    />
                    <span style={suffixSt}>%</span>
                  </div>
                </div>

                <input
                  type="range"
                  className="sip-slider"
                  min={1} max={30} step={0.1}
                  value={returnRate}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    setReturnRateStr(String(v));
                  }}
                  style={{ background: sliderBg(returnRate, 1, 30) }}
                />

                {/* Preset pointer buttons */}
                <div style={{ display: 'flex', gap: '7px', marginTop: '10px', flexWrap: 'wrap' }}>
                  {RETURN_PRESETS.map((preset) => {
                    const active = returnRate === preset;
                    return (
                      <button
                        key={preset}
                        onClick={() => setReturnRateStr(String(preset))}
                        style={{ fontFamily: 'Onest, sans-serif', fontSize: '12px', fontWeight: 600, padding: '4px 13px', borderRadius: '50px', border: active ? 'none' : '1.5px solid #e5e7eb', cursor: 'pointer', backgroundColor: active ? '#115e59' : '#f9fafb', color: active ? '#ffffff' : '#6b7280', transition: 'all 0.15s ease' }}
                      >
                        {preset}%
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ── Time period slider ── */}
              <SliderRow
                label="Time Period"
                value={timePeriod}
                min={1} max={40} step={1}
                displaySuffix="Yr" inputWidth="36px"
                minLabel="1 Yr" maxLabel="40 Yr"
                onSlider={setTimePeriod}
                onInput={setPeriod}
                sliderBg={sliderBg}
                isLast
                labelSt={labelSt} valueSt={valueSt} suffixSt={suffixSt} minMaxSt={minMaxSt} inputBoxSt={inputBoxSt}
              />

              {/* Divider */}
              <div style={{ borderTop: '1.5px solid #f3f4f6', margin: '32px 0' }} />

              {/* ── Chart + Results ── */}
              <div className="sip-chart-row" style={{ display: 'flex', alignItems: 'center', gap: '36px' }}>

                {/* Donut */}
                <div className="sip-chart-container">
                  <svg viewBox="0 0 200 200" width="180" height="180">
                    <g transform={`rotate(-90, ${CX}, ${CY})`}>
                      <circle cx={CX} cy={CY} r={R} fill="none" stroke="#f3f4f6" strokeWidth="28" />
                      <circle cx={CX} cy={CY} r={R} fill="none" stroke="#115e59" strokeWidth="28"
                        strokeDasharray={`${invDash} ${circ}`} strokeLinecap="butt" />
                      <circle cx={CX} cy={CY} r={R} fill="none" stroke="#4ade80" strokeWidth="28"
                        strokeDasharray={`${retDash} ${circ}`} strokeDashoffset={-(invDash + gap)} strokeLinecap="butt" />
                    </g>
                    <text x={CX} y={CY - 8}  textAnchor="middle" style={{ fontFamily: 'Onest, sans-serif', fontSize: '11px', fill: '#9ca3af', fontWeight: 500 }}>Total Value</text>
                    <text x={CX} y={CY + 14} textAnchor="middle" style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', fill: '#111827', fontWeight: 700 }}>
                      {totalValue >= 10000000 ? `₹${(totalValue / 10000000).toFixed(1)}Cr`
                        : totalValue >= 100000 ? `₹${(totalValue / 100000).toFixed(1)}L`
                        : `₹${(totalValue / 1000).toFixed(0)}K`}
                    </text>
                  </svg>
                </div>

                {/* Results */}
                <div style={{ flex: 1 }}>
                  {[
                    { label: 'Invested Amount', value: investedAmount, dot: '#115e59' },
                    { label: 'Est. Returns',    value: estReturns,    dot: '#4ade80' },
                  ].map(({ label, value, dot }) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: dot, flexShrink: 0 }} />
                        <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>{label}</span>
                      </div>
                      <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', fontWeight: 700, color: '#111827' }}>{fmt(value)}</span>
                    </div>
                  ))}

                  <div style={{ borderTop: '1px solid #f3f4f6', margin: '16px 0' }} />

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', color: '#111827', fontWeight: 600 }}>Total Value</span>
                    <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '22px', fontWeight: 800, color: '#115e59', letterSpacing: '-0.02em' }}>{fmt(totalValue)}</span>
                  </div>

                  <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '11px', color: '#9ca3af', marginTop: '8px', lineHeight: 1.5 }}>
                    {mode === 'sip'
                      ? `₹${monthlyAmount.toLocaleString('en-IN')}/mo · ${timePeriod} yr · ${returnRate}% p.a.`
                      : `₹${lumpsumAmount.toLocaleString('en-IN')} · ${timePeriod} yr · ${returnRate}% p.a.`}
                  </p>
                </div>
              </div>

            </div>
          </div>

          {/* RIGHT — Sidebar */}
          <SmartCalculatorsSidebar currentSlug="sip" rightOffset="100px" />
        </div>
      </div>

      {/* ── SEO Content Section ── */}
      <div className="sip-seo">

        {/* Main Intro */}
        <div style={{ marginBottom: '48px' }}>
          <h2 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '36px', color: '#1f2937', marginBottom: '16px' }}>
            SIP Calculator – Calculate Your Systematic Investment Plan Returns Online
          </h2>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '16px', color: '#6b7280', lineHeight: 1.7, marginBottom: '16px' }}>
            A SIP Calculator is a smart financial planning tool that helps you estimate the future value of your mutual fund investments made through a Systematic Investment Plan (SIP). Instead of guessing how your money might grow, this calculator gives you a clear projection based on your monthly investment amount, investment duration, and expected rate of return.
          </p>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '16px', color: '#6b7280', lineHeight: 1.7, marginBottom: '24px' }}>
            Whether you are planning for long-term wealth creation, retirement, a house, or financial freedom, a SIP return calculator helps you plan with confidence.
          </p>
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={{ fontFamily: 'Onest, sans-serif', backgroundColor: '#115e59', color: '#ffffff', padding: '14px 32px', borderRadius: '8px', fontWeight: 600, fontSize: '15px', border: 'none', cursor: 'pointer' }}>
            Calculate Your SIP Returns
          </button>
        </div>

        {/* What is SIP */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '20px', color: '#1f2937', marginBottom: '16px' }}>What is a SIP (Systematic Investment Plan)?</h3>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '16px', color: '#6b7280', lineHeight: 1.7, marginBottom: '16px' }}>
            A Systematic Investment Plan (SIP) allows you to invest a fixed amount in mutual funds at regular intervals, usually monthly. Instead of investing a lump sum, SIP helps you build wealth gradually and consistently.
          </p>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '16px', color: '#6b7280', lineHeight: 1.7, marginBottom: '16px' }}>
            SIP investing works on two powerful principles:
          </p>
          <div style={{ marginBottom: '12px', padding: '20px', backgroundColor: '#f9fafb', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
            <h4 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '16px', color: '#1f2937', marginBottom: '8px', marginTop: 0 }}>1. Power of Compounding</h4>
            <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', color: '#6b7280', lineHeight: 1.7, margin: 0 }}>
              Your returns generate additional returns over time. The longer you stay invested, the more your wealth compounds.
            </p>
          </div>
          <div style={{ padding: '20px', backgroundColor: '#f9fafb', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
            <h4 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '16px', color: '#1f2937', marginBottom: '8px', marginTop: 0 }}>2. Rupee Cost Averaging</h4>
            <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', color: '#6b7280', lineHeight: 1.7, margin: 0 }}>
              When markets are high, you buy fewer units. When markets fall, you buy more units. Over time, this averages your purchase cost and reduces the impact of volatility.
            </p>
          </div>
        </div>

        {/* What is a SIP Calculator */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '20px', color: '#1f2937', marginBottom: '16px' }}>What is a SIP Calculator?</h3>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '16px', color: '#6b7280', lineHeight: 1.7, marginBottom: '16px' }}>
            A SIP Calculator online helps you:
          </p>
          <ul style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', color: '#374151', lineHeight: 1.8, paddingLeft: '0', listStyle: 'none' }}>
            {[
              { text: 'Estimate your maturity amount',           icon: <BarChart2   size={15} color="#115e59" strokeWidth={2} /> },
              { text: 'Calculate total investment made',         icon: <IndianRupee size={15} color="#115e59" strokeWidth={2} /> },
              { text: 'See expected returns earned',             icon: <TrendingUp  size={15} color="#115e59" strokeWidth={2} /> },
              { text: 'Plan for financial goals',                icon: <Target      size={15} color="#115e59" strokeWidth={2} /> },
              { text: 'Compare different investment scenarios',  icon: <Shuffle     size={15} color="#115e59" strokeWidth={2} /> },
            ].map(({ text, icon }, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                <span style={{ width: '28px', height: '28px', borderRadius: '6px', backgroundColor: '#E6F2F1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {icon}
                </span>
                {text}
              </li>
            ))}
          </ul>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', color: '#9ca3af', lineHeight: 1.7, marginTop: '16px' }}>
            It gives you a projected future value based on assumed annual returns. Actual returns may vary depending on market performance and fund selection.
          </p>
        </div>

        {/* How Does a SIP Calculator Work */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '20px', color: '#1f2937', marginBottom: '16px' }}>How Does a SIP Calculator Work?</h3>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '16px', color: '#6b7280', lineHeight: 1.7, marginBottom: '20px' }}>
            The SIP maturity amount is calculated using the compound interest formula:
          </p>
          <div style={{ backgroundColor: '#115e59', padding: '24px', borderRadius: '16px', marginBottom: '20px' }}>
            <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '18px', fontWeight: 700, color: '#ffffff', marginBottom: '20px', fontStyle: 'italic' }}>
              M = P &times; &#123;[1 + i]ⁿ &ndash; 1&#125; / i &times; (1 + i)
            </p>
            <div className="seo-grid-2">
              {[['M', 'Maturity amount'], ['P', 'Monthly investment amount'], ['i', 'Monthly rate of return'], ['n', 'Total number of monthly investments']].map(([sym, desc]) => (
                <div key={sym} style={{ backgroundColor: 'rgba(255,255,255,0.12)', padding: '10px 14px', borderRadius: '8px' }}>
                  <span style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, color: '#4ade80', fontSize: '16px' }}>{sym}</span>
                  <span style={{ fontFamily: 'Onest, sans-serif', color: 'rgba(255,255,255,0.85)', fontSize: '14px', marginLeft: '8px' }}>= {desc}</span>
                </div>
              ))}
            </div>
          </div>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', color: '#6b7280', lineHeight: 1.7 }}>
            To convert annual return into monthly return correctly: <strong>Monthly Rate = (1 + Annual Return)^(1/12) &ndash; 1</strong>. This ensures accurate compounding instead of simply dividing the annual rate by 12.
          </p>
        </div>

        {/* Example */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '20px', color: '#1f2937', marginBottom: '16px' }}>Example: SIP Investment Calculation</h3>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '16px', color: '#6b7280', lineHeight: 1.7, marginBottom: '16px' }}>
            If you invest <strong>&#8377;5,000 per month</strong> for <strong>15 years</strong> at an expected annual return of <strong>12%</strong>:
          </p>
          <div style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #e5e7eb', marginBottom: '16px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#115e59' }}>
                  <th style={{ fontFamily: 'Onest, sans-serif', textAlign: 'left', color: '#fff', fontWeight: 600, padding: '14px 16px' }}>Parameter</th>
                  <th style={{ fontFamily: 'Onest, sans-serif', textAlign: 'right', color: '#fff', fontWeight: 600, padding: '14px 16px' }}>Value</th>
                </tr>
              </thead>
              <tbody>
                {[['Total Investment', '₹9,00,000'], ['Estimated Corpus', '~₹25–27 Lakhs (approx.)'], ['Wealth Gained', '~₹16–18 Lakhs']].map(([label, val], i) => (
                  <tr key={i} style={{ backgroundColor: i % 2 === 1 ? '#f9fafb' : '#fff', borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ fontFamily: 'Onest, sans-serif', padding: '12px 16px', color: '#374151', fontSize: '15px' }}>{label}</td>
                    <td style={{ fontFamily: 'Onest, sans-serif', padding: '12px 16px', color: '#1f2937', fontSize: '15px', fontWeight: 600, textAlign: 'right' }}>{val}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', color: '#6b7280', lineHeight: 1.7 }}>
            The key factor here is time. Starting early significantly reduces the monthly amount required to achieve your target corpus.
          </p>
        </div>

        {/* Benefits */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '20px', color: '#1f2937', marginBottom: '16px' }}>Benefits of Using Our SIP Return Calculator</h3>
          <ul style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', color: '#374151', lineHeight: 1.8, paddingLeft: '0', listStyle: 'none' }}>
            {['Instant wealth projection', 'Helps plan long-term financial goals', 'Shows total invested amount vs estimated returns', 'Saves time compared to manual calculations', 'Allows scenario testing by adjusting tenure and return rate'].map((item, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <span style={{ width: '28px', height: '28px', borderRadius: '6px', backgroundColor: '#E6F2F1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#115e59" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                </span>
                {item}
              </li>
            ))}
          </ul>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', color: '#6b7280', lineHeight: 1.7, marginTop: '8px' }}>
            You can easily reverse calculate how much you need to invest monthly to reach a specific financial target.
          </p>
        </div>

        {/* How to Use */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '20px', color: '#1f2937', marginBottom: '20px' }}>How to Use the SIP Calculator?</h3>
          <div className="seo-grid-2">
            {[
              { s: '1', t: 'Enter your monthly SIP amount' },
              { s: '2', t: 'Select investment duration (in years)' },
              { s: '3', t: 'Enter expected annual return' },
              { s: '4', t: 'Instantly view your projected corpus and returns' },
            ].map((item) => (
              <div key={item.s} style={{ backgroundColor: '#f9fafb', padding: '20px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px', border: '1px solid #e5e7eb' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#115e59', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontFamily: 'Onest, sans-serif', color: '#fff', fontWeight: 600, fontSize: '14px' }}>{item.s}</span>
                </div>
                <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', color: '#374151', fontWeight: 500 }}>{item.t}</span>
              </div>
            ))}
          </div>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', color: '#6b7280', lineHeight: 1.7, marginTop: '16px' }}>
            Adjust the values to compare different scenarios and build a realistic investment plan.
          </p>
        </div>

        {/* Who Should Use */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '20px', color: '#1f2937', marginBottom: '16px' }}>Who Should Use a SIP Calculator?</h3>
          <ul style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', color: '#374151', lineHeight: 1.8, paddingLeft: '0', listStyle: 'none' }}>
            {[
              { text: 'First-time investors',                    icon: <Sprout   size={15} color="#115e59" strokeWidth={2} /> },
              { text: 'Long-term wealth builders',               icon: <Layers   size={15} color="#115e59" strokeWidth={2} /> },
              { text: 'Retirement planners',                     icon: <Clock    size={15} color="#115e59" strokeWidth={2} /> },
              { text: 'Parents planning education funds',        icon: <BookOpen size={15} color="#115e59" strokeWidth={2} /> },
              { text: 'Investors comparing SIP vs lump sum',     icon: <Scale    size={15} color="#115e59" strokeWidth={2} /> },
              { text: 'Anyone serious about disciplined investing', icon: <Star  size={15} color="#115e59" strokeWidth={2} /> },
            ].map(({ text, icon }, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                <span style={{ width: '28px', height: '28px', borderRadius: '6px', backgroundColor: '#E6F2F1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {icon}
                </span>
                {text}
              </li>
            ))}
          </ul>
        </div>

        {/* Important Note */}
        <div style={{ marginBottom: '48px', backgroundColor: '#fff7ed', padding: '24px', borderRadius: '16px', border: '1px solid #fed7aa' }}>
          <h3 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '18px', color: '#92400e', marginBottom: '12px', marginTop: 0 }}>Important Note</h3>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', color: '#78350f', lineHeight: 1.7, marginBottom: '12px' }}>
            SIP returns are market-linked and not guaranteed. The calculator provides estimated results based on assumed return rates. Actual returns may vary depending on market conditions, fund performance, expense ratio, and taxation.
          </p>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', color: '#78350f', lineHeight: 1.7, margin: 0 }}>
            Mutual fund investments are subject to market risk. Please read all scheme-related documents carefully before investing.
          </p>
        </div>

        {/* FAQs — Accordion */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '20px', color: '#1f2937', marginBottom: '24px' }}>Frequently Asked Questions (FAQs)</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[
              { q: 'What is a SIP Calculator?', a: 'A SIP Calculator is an online financial tool that helps you estimate the future value of your mutual fund investments made through a Systematic Investment Plan (SIP). It calculates the expected maturity amount based on your monthly investment, tenure, and assumed rate of return.' },
              { q: 'How accurate is a SIP return calculator?', a: 'A SIP return calculator provides an estimated projection based on assumed annual returns and compounding. Actual returns may vary depending on market performance, fund selection, expense ratio, and taxation. It is meant for planning purposes, not guaranteed outcomes.' },
              { q: 'How is SIP maturity amount calculated?', a: 'The SIP maturity amount is calculated using a compound interest formula: M = P × ({[1 + i]ⁿ – 1} / i) × (1 + i). Where P = Monthly investment, i = Monthly rate of return, n = Number of monthly installments. The calculator converts annual returns into an effective monthly rate to ensure accurate compounding.' },
              { q: 'Can I calculate how much SIP I need to reach a target amount?', a: 'Yes. You can reverse calculate your required monthly SIP by adjusting the investment amount, tenure, and expected return until the projected corpus matches your financial goal.' },
              { q: 'What is the minimum amount required to start a SIP?', a: 'Most mutual funds allow you to start a SIP with as little as ₹500 per month. However, the ideal investment amount depends on your financial goals and investment horizon.' },
              { q: 'What is the maximum tenure for a SIP?', a: 'There is no strict maximum tenure. You can continue a SIP for as long as your financial goals require. Longer tenures generally benefit more from compounding.' },
              { q: 'Is SIP better than lump sum investment?', a: 'SIP and lump sum are two different investment methods. SIP helps with disciplined investing and rupee cost averaging, especially in volatile markets. Lump sum investments may be suitable when markets are relatively stable or undervalued. The right choice depends on your financial situation and risk appetite.' },
              { q: 'Can I increase or modify my SIP amount later?', a: 'Yes. Most mutual funds allow you to increase, decrease, pause, or stop your SIP based on your financial needs. Some platforms also offer step-up SIP options.' },
              { q: 'Does SIP guarantee fixed returns?', a: 'No. SIP investments are market-linked and do not guarantee fixed returns. Returns depend on the performance of the mutual fund scheme and overall market conditions.' },
              { q: 'Why should I use an online SIP calculator before investing?', a: 'Using a SIP calculator helps you understand potential wealth creation, compare different investment scenarios, plan long-term financial goals, and make informed investment decisions. It brings clarity before you commit your money.' },
            ].map(({ q, a }, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={i} style={{ borderRadius: '12px', border: `1px solid ${isOpen ? '#115e59' : '#e5e7eb'}`, overflow: 'hidden', transition: 'border-color 0.2s ease' }}>
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', backgroundColor: isOpen ? '#f0faf9' : '#f9fafb', border: 'none', cursor: 'pointer', textAlign: 'left', gap: '12px' }}
                  >
                    <span style={{ fontFamily: 'Onest, sans-serif', fontWeight: 600, fontSize: '15px', color: isOpen ? '#115e59' : '#1f2937' }}>{q}</span>
                    <span style={{ flexShrink: 0, width: '26px', height: '26px', borderRadius: '50%', backgroundColor: isOpen ? '#115e59' : '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease' }}>
                      <ChevronDown size={14} color={isOpen ? '#fff' : '#6b7280'} style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
                    </span>
                  </button>
                  {isOpen && (
                    <div style={{ padding: '0 20px 18px 20px', backgroundColor: '#fff' }}>
                      <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', color: '#6b7280', lineHeight: 1.7, margin: 0 }}>{a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

      </div>
      {/* End SEO Section */}

    </div>
  );
};

// ── Reusable slider row ────────────────────────────────────────────────────────
const SliderRow = ({
  label, value, displayValue, min, max, step,
  displayPrefix, displaySuffix, inputWidth = '72px',
  minLabel, maxLabel,
  onSlider, onInput, sliderBg, isLast,
  labelSt, valueSt, suffixSt, minMaxSt, inputBoxSt,
}) => (
  <div style={{ marginBottom: isLast ? 0 : '30px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
      <label style={labelSt}>{label}</label>
      <div style={inputBoxSt}>
        {displayPrefix && <span style={suffixSt}>{displayPrefix}</span>}
        <input
          type="text"
          inputMode="numeric"
          className="no-spin"
          value={displayValue !== undefined ? displayValue : value}
          onChange={(e) => onInput(e.target.value)}
          style={{ ...valueSt, width: inputWidth }}
        />
        {displaySuffix && <span style={suffixSt}>{displaySuffix}</span>}
      </div>
    </div>
    <input
      type="range"
      className="sip-slider"
      min={min} max={max} step={step}
      value={value}
      onChange={(e) => onSlider(step < 1 ? parseFloat(e.target.value) : parseInt(e.target.value))}
      style={{ background: sliderBg(value, min, max) }}
    />
    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
      <span style={minMaxSt}>{minLabel}</span>
      <span style={minMaxSt}>{maxLabel}</span>
    </div>
  </div>
);

export default SIPCalculator;
